import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    storeCount: '',
    currentPOS: '',
    interest: '',
    requirements: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const interestOptions = [
    { value: 'cloud', label: 'Cloud Solution' },
    { value: 'desktop', label: 'Desktop POS' },
    { value: 'both', label: 'Both Solutions' }
  ];

  const storeCountOptions = [
    { value: '1', label: '1 Store' },
    { value: '2-5', label: '2-5 Stores' },
    { value: '6-10', label: '6-10 Stores' },
    { value: '10+', label: 'More than 10 Stores' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.businessName?.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!formData?.contactName?.trim()) {
      newErrors.contactName = 'Contact name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-\+\(\)]{10,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.interest) {
      newErrors.interest = 'Please select your interest';
    }

    if (!formData?.storeCount) {
      newErrors.storeCount = 'Please select number of stores';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/support@cultaz.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'contact',
          businessName: formData.businessName,
          contactName: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          storeCount: formData.storeCount,
          currentPOS: formData.currentPOS,
          interest: formData.interest,
          requirements: formData.requirements
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          businessName: '',
          contactName: '',
          email: '',
          phone: '',
          storeCount: '',
          currentPOS: '',
          interest: '',
          requirements: ''
        });
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        alert('Failed to send message. Please try again later.');
      }
    } catch (err) {
      alert('An error occurred while sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl shadow-purple-lg border border-border p-6 md:p-8 lg:p-10">
      <div className="mb-6 md:mb-8">
        <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3">
          Get in Touch
        </h2>
        <p className="font-body text-base md:text-lg text-muted-foreground">
          Fill out the form below and our team will get back to you within 24 hours.
        </p>
      </div>
      {submitSuccess && (
        <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start space-x-3">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" strokeWidth={2} />
          <div>
            <p className="font-body text-sm md:text-base text-success font-medium">
              Message sent successfully!
            </p>
            <p className="font-body text-sm text-success/80 mt-1">
              We'll contact you within 24 hours.
            </p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <Input
            label="Business Name"
            type="text"
            name="businessName"
            placeholder="Enter your business name"
            value={formData?.businessName}
            onChange={handleChange}
            error={errors?.businessName}
            required
          />

          <Input
            label="Contact Name"
            type="text"
            name="contactName"
            placeholder="Enter your full name"
            value={formData?.contactName}
            onChange={handleChange}
            error={errors?.contactName}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={handleChange}
            error={errors?.email}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            name="phone"
            placeholder="+234 XXX XXX XXXX"
            value={formData?.phone}
            onChange={handleChange}
            error={errors?.phone}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <Select
            label="I'm Interested In"
            placeholder="Select your interest"
            options={interestOptions}
            value={formData?.interest}
            onChange={(value) => handleSelectChange('interest', value)}
            error={errors?.interest}
            required
          />

          <Select
            label="Number of Stores"
            placeholder="Select store count"
            options={storeCountOptions}
            value={formData?.storeCount}
            onChange={(value) => handleSelectChange('storeCount', value)}
            error={errors?.storeCount}
            required
          />
        </div>

        <Input
          label="Current POS System (Optional)"
          type="text"
          name="currentPOS"
          placeholder="e.g., Manual, Excel, Other software"
          value={formData?.currentPOS}
          onChange={handleChange}
        />

        <div>
          <label className="block font-body text-sm md:text-base font-medium text-foreground mb-2">
            Specific Requirements (Optional)
          </label>
          <textarea
            name="requirements"
            rows="4"
            placeholder="Tell us about your specific needs, challenges, or questions..."
            value={formData?.requirements}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:border-primary transition-smooth resize-none"
          />
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;